import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Check, CheckCircle2, ChevronDown, ChevronRight, Pencil } from "lucide-react";
import SalesProductEntryList, { SALES_LAST_GROUP_KEY } from "./SalesProductEntryList";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";
const DATE_CONFIG_STORAGE_KEY = "salesHistoryDateConfig";
const HISTORY_MAX_RANGE_DAYS = 90;

type DatePreset = "today" | "yesterday" | "last7" | "last30" | "last90" | "custom";
type DateConfig = {
  preset: DatePreset;
  customStart?: string;
  customEnd?: string;
};

const PRESET_OPTIONS: Array<{ id: DatePreset; label: string }> = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
  { id: "last90", label: "Last 90 days" },
  { id: "custom", label: "Custom date range" },
];

function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function dateKeyToUTCDate(key: string) {
  const [y, m, dd] = key.split("-").map((x) => Number(x));
  return new Date(Date.UTC(y, m - 1, dd));
}

function shiftDateKey(key: string, days: number) {
  const d = dateKeyToUTCDate(key);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDateKeyLabel(dateKey: string) {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) return dateKey;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: MALAYSIA_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

function weekdayAbbrFromDateKey(dateKey: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: MALAYSIA_TZ,
    weekday: "short",
  }).format(dateKeyToUTCDate(dateKey));
}

function formatDateWithWeekday(dateKey: string) {
  return `${formatDateKeyLabel(dateKey)} (${weekdayAbbrFromDateKey(dateKey)})`;
}

function formatRangeLabel(startKey: string, endKey: string) {
  if (startKey === endKey) return formatDateKeyLabel(startKey);
  return `${formatDateKeyLabel(startKey)} – ${formatDateKeyLabel(endKey)}`;
}

function formatMoney(v: number) {
  return `RM ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function inclusiveDayCount(startKey: string, endKey: string) {
  const start = dateKeyToUTCDate(startKey).getTime();
  const end = dateKeyToUTCDate(endKey).getTime();
  return Math.round((end - start) / 86400000) + 1;
}

function isDateKey(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function loadStoredDateConfig(): DateConfig {
  try {
    const raw = localStorage.getItem(DATE_CONFIG_STORAGE_KEY);
    if (!raw) return { preset: "today" };
    const parsed = JSON.parse(raw) as DateConfig;
    const presetIds = PRESET_OPTIONS.map((o) => o.id);
    if (!presetIds.includes(parsed?.preset)) return { preset: "today" };
    return {
      preset: parsed.preset,
      customStart: isDateKey(parsed.customStart) ? parsed.customStart : undefined,
      customEnd: isDateKey(parsed.customEnd) ? parsed.customEnd : undefined,
    };
  } catch {
    return { preset: "today" };
  }
}

function persistDateConfig(config: DateConfig) {
  localStorage.setItem(DATE_CONFIG_STORAGE_KEY, JSON.stringify(config));
}

function resolveDateRange(config: DateConfig, todayKey: string): { startDate: string; endDate: string } {
  if (config.preset === "today") return { startDate: todayKey, endDate: todayKey };
  if (config.preset === "yesterday") {
    const yesterday = shiftDateKey(todayKey, -1);
    return { startDate: yesterday, endDate: yesterday };
  }
  if (config.preset === "last7") return { startDate: shiftDateKey(todayKey, -6), endDate: todayKey };
  if (config.preset === "last30") return { startDate: shiftDateKey(todayKey, -29), endDate: todayKey };
  if (config.preset === "last90") return { startDate: shiftDateKey(todayKey, -89), endDate: todayKey };

  let start = isDateKey(config.customStart) ? config.customStart : shiftDateKey(todayKey, -6);
  let end = isDateKey(config.customEnd) ? config.customEnd : todayKey;
  if (end > todayKey) end = todayKey;
  if (start > end) start = end;
  if (inclusiveDayCount(start, end) > HISTORY_MAX_RANGE_DAYS) {
    start = shiftDateKey(end, -(HISTORY_MAX_RANGE_DAYS - 1));
  }
  return { startDate: start, endDate: end };
}

function presetLabel(preset: DatePreset) {
  return PRESET_OPTIONS.find((o) => o.id === preset)?.label ?? "Today";
}

type SalesProductRow = {
  productId: string;
  name: string;
  sku: string | null;
  qtySold: number;
  tradeInQty?: number;
  sellingPrice: number | null;
  productImageUrl?: string | null;
  productImageVersion?: string | null;
  value: number | null;
  allowTradeIn?: boolean;
};

type SalesGroupRow = { groupName: string; products: SalesProductRow[] };
type SalesSubmissionInfo = {
  isSubmitted: boolean;
  bankedAt: string | null;
  expectedTotal: number;
  remainingAmount: number;
  addedByAdmin?: boolean;
};
type HistoryDay = {
  salesDate: string;
  bankedAt: string | null;
  totalQty: number;
  totalTradeInQty: number;
  totalValue: number;
  remainingAmount: number;
  canAmend?: boolean;
  addedByAdmin?: boolean;
};
type HistoryResponse = {
  team: { id: string; name: string } | null;
  startDate: string;
  endDate: string;
  dayCount: number;
  submittedDayCount: number;
  editableSubmittedLimit?: number;
  days: HistoryDay[];
};
type TeamSalesResponse = {
  team: { id: string; name: string } | null;
  groups: SalesGroupRow[];
  salesSubmission?: SalesSubmissionInfo;
  canAmend?: boolean;
};

export default function SalesHistory() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";
  const todayDateKey = useMemo(() => malaysiaDateKeyFromDate(new Date()), []);

  const [dateConfig, setDateConfig] = useState<DateConfig>(() => loadStoredDateConfig());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draftConfig, setDraftConfig] = useState<DateConfig>(dateConfig);
  const [sheetError, setSheetError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailSales, setDetailSales] = useState<TeamSalesResponse | null>(null);
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const [editing, setEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeGroupName, setActiveGroupName] = useState("");
  const [qtySoldByProductId, setQtySoldByProductId] = useState<Record<string, number>>({});
  const [tradeInQtyByProductId, setTradeInQtyByProductId] = useState<Record<string, number>>({});
  const [availableQtyByProductId, setAvailableQtyByProductId] = useState<Record<string, number>>({});
  const [brokenImageProductIds, setBrokenImageProductIds] = useState<Record<string, boolean>>({});
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [amendReason, setAmendReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  const appliedRange = useMemo(
    () => resolveDateRange(dateConfig, todayDateKey),
    [dateConfig, todayDateKey]
  );

  useEffect(() => {
    if (!sheetOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [sheetOpen]);

  useEffect(() => {
    setSelectedDateKey(null);
    setDetailSales(null);
    setDetailError("");
    setEditing(false);
    setSaveError("");
    setSaveSuccess("");
  }, [appliedRange.startDate, appliedRange.endDate]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!token) {
        setError("Not authenticated. Please login again.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          startDate: appliedRange.startDate,
          endDate: appliedRange.endDate,
        });
        const res = await fetch(`${apiUrl}/supervisor/team-sales-history?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: HistoryResponse = await res.json().catch(() => ({}) as HistoryResponse);
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load sales (${res.status}).`);
          setHistory(null);
          return;
        }
        setHistory(json);
      } catch {
        setError("Unable to connect to server. Please try again.");
        setHistory(null);
      } finally {
        setLoading(false);
      }
    };
    void loadHistory();
  }, [apiUrl, token, appliedRange.startDate, appliedRange.endDate]);

  useEffect(() => {
    if (!selectedDateKey) {
      setDetailSales(null);
      setDetailError("");
      return;
    }
    const loadDetail = async () => {
      if (!token) return;
      setDetailLoading(true);
      setDetailError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamSalesResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) {
          setDetailError((json as any)?.message || `Failed to load sales (${res.status}).`);
          setDetailSales(null);
          return;
        }
        setDetailSales(json);
      } catch {
        setDetailError("Unable to connect to server. Please try again.");
        setDetailSales(null);
      } finally {
        setDetailLoading(false);
      }
    };
    void loadDetail();
  }, [apiUrl, token, selectedDateKey, detailReloadKey]);

  useEffect(() => {
    const loadAvailableStock = async () => {
      if (!token || !selectedDateKey) return;
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-stock?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({ groups: [] }));
        if (!res.ok) return;
        const nextAvailable: Record<string, number> = {};
        for (const g of json.groups ?? []) {
          for (const p of g.products ?? []) {
            nextAvailable[p.productId] = p.openingQty ?? 0;
          }
        }
        setAvailableQtyByProductId(nextAvailable);
      } catch {
        setAvailableQtyByProductId({});
      }
    };
    void loadAvailableStock();
  }, [apiUrl, token, selectedDateKey]);

  useEffect(() => {
    setEditing(false);
    setShowReasonModal(false);
    setSaveError("");
  }, [selectedDateKey]);

  const canAmend = Boolean(detailSales?.canAmend);
  const allDetailProducts = useMemo(
    () => (detailSales?.groups ?? []).flatMap((g) => g.products ?? []),
    [detailSales]
  );
  const originalQtyByProductId = useMemo(() => {
    const map: Record<string, number> = {};
    for (const p of allDetailProducts) map[p.productId] = p.qtySold ?? 0;
    return map;
  }, [allDetailProducts]);
  const originalTradeInByProductId = useMemo(() => {
    const map: Record<string, number> = {};
    for (const p of allDetailProducts) map[p.productId] = p.tradeInQty ?? 0;
    return map;
  }, [allDetailProducts]);

  const summaryGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (detailSales?.groups ?? [])
      .map((g) => ({
        groupName: g.groupName,
        products: (g.products ?? []).filter((p) => {
          const qty = editing ? qtySoldByProductId[p.productId] ?? p.qtySold ?? 0 : p.qtySold ?? 0;
          const tradeIn = editing ? tradeInQtyByProductId[p.productId] ?? p.tradeInQty ?? 0 : p.tradeInQty ?? 0;
          const matchesSearch =
            !q ||
            (p.name ?? "").toLowerCase().includes(q) ||
            (p.sku ?? "").toLowerCase().includes(q);
          if (!matchesSearch) return false;
          if (editing) {
            const originallySold = (p.qtySold ?? 0) > 0 || (p.tradeInQty ?? 0) > 0;
            return originallySold || qty > 0 || tradeIn > 0;
          }
          return qty > 0 || tradeIn > 0;
        }),
      }))
      .filter((g) => g.products.length > 0);
  }, [detailSales, editing, searchQuery, qtySoldByProductId, tradeInQtyByProductId]);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    for (const g of summaryGroups) {
      for (const p of g.products) {
        const qty = editing ? qtySoldByProductId[p.productId] ?? p.qtySold ?? 0 : p.qtySold ?? 0;
        totalQty += qty;
        if (typeof p.sellingPrice === "number") totalValue += qty * p.sellingPrice;
        else if (!editing && typeof p.value === "number") totalValue += p.value;
      }
    }
    return { totalQty, totalValue };
  }, [summaryGroups, editing, qtySoldByProductId]);

  const salesSubmission = detailSales?.salesSubmission;
  const isSubmitted = Boolean(salesSubmission?.isSubmitted);
  const remainingAmount = salesSubmission?.remainingAmount ?? 0;
  const isFullySettled = isSubmitted && remainingAmount <= 0.004;
  const addedByAdmin = Boolean(salesSubmission?.addedByAdmin);
  const submittedDays = history?.days ?? [];
  const hasAmendChanges = allDetailProducts.some((p) => {
    const nextQty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
    const nextTradeIn = tradeInQtyByProductId[p.productId] ?? p.tradeInQty ?? 0;
    return nextQty !== (p.qtySold ?? 0) || nextTradeIn !== (p.tradeInQty ?? 0);
  });

  const startEditing = () => {
    const nextQty: Record<string, number> = {};
    const nextTradeIn: Record<string, number> = {};
    for (const p of allDetailProducts) {
      nextQty[p.productId] = p.qtySold ?? 0;
      nextTradeIn[p.productId] = p.tradeInQty ?? 0;
    }
    setQtySoldByProductId(nextQty);
    setTradeInQtyByProductId(nextTradeIn);
    const names = (detailSales?.groups ?? []).map((g) => g.groupName);
    const remembered = localStorage.getItem(SALES_LAST_GROUP_KEY) || "";
    setActiveGroupName(names.includes(remembered) ? remembered : names[0] ?? "");
    setSearchQuery("");
    setSaveError("");
    setSaveSuccess("");
    setAmendReason("");
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setShowReasonModal(false);
    setAmendReason("");
    setSaveError("");
    setSearchQuery("");
    setActiveGroupName("");
  };

  const submitAmend = async () => {
    if (!selectedDateKey || !token) return;
    const reason = amendReason.trim();
    if (!reason) {
      setSaveError("Please enter a reason for this amendment.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const originalIds = new Set(
        allDetailProducts.filter((p) => (p.qtySold ?? 0) > 0 || (p.tradeInQty ?? 0) > 0).map((p) => p.productId)
      );
      const productIds = new Set([...Object.keys(qtySoldByProductId), ...Object.keys(tradeInQtyByProductId), ...originalIds]);
      const lines = Array.from(productIds)
        .map((productId) => ({
          productId,
          qtySold: Math.max(0, Math.floor(qtySoldByProductId[productId] ?? originalQtyByProductId[productId] ?? 0)),
          tradeInQty: Math.max(0, Math.floor(tradeInQtyByProductId[productId] ?? originalTradeInByProductId[productId] ?? 0)),
        }))
        .filter((line) => line.qtySold > 0 || line.tradeInQty > 0 || originalIds.has(line.productId));

      const res = await fetch(`${apiUrl}/supervisor/team-sales/amend`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          salesDate: selectedDateKey,
          reason,
          lines,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(json?.message || `Failed to save (${res.status}).`);
        return;
      }
      setShowReasonModal(false);
      setAmendReason("");
      setEditing(false);
      setSaveSuccess("Sales updated. Stock and bank-in difference were recalculated.");
      setDetailReloadKey((k) => k + 1);
      const params = new URLSearchParams({
        startDate: appliedRange.startDate,
        endDate: appliedRange.endDate,
      });
      const listRes = await fetch(`${apiUrl}/supervisor/team-sales-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const listJson: HistoryResponse = await listRes.json().catch(() => ({}) as HistoryResponse);
      if (listRes.ok) setHistory(listJson);
    } catch {
      setSaveError("Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openSheet = () => {
    const current = loadStoredDateConfig();
    const range = resolveDateRange(current, todayDateKey);
    setDraftConfig({
      ...current,
      customStart: current.customStart ?? range.startDate,
      customEnd: current.customEnd ?? range.endDate,
    });
    setSheetError("");
    setSheetOpen(true);
  };

  const applyDateConfig = (next: DateConfig) => {
    persistDateConfig(next);
    setDateConfig(next);
    setSheetError("");
    setSheetOpen(false);
  };

  const applyDraftConfig = () => {
    let next: DateConfig = { ...draftConfig };
    if (next.preset === "custom") {
      let start = isDateKey(next.customStart) ? next.customStart : appliedRange.startDate;
      let end = isDateKey(next.customEnd) ? next.customEnd : appliedRange.endDate;
      if (end > todayDateKey) end = todayDateKey;
      if (start > end) {
        setSheetError("Start date must be on or before end date.");
        return;
      }
      if (inclusiveDayCount(start, end) > HISTORY_MAX_RANGE_DAYS) {
        setSheetError(`Date range cannot exceed ${HISTORY_MAX_RANGE_DAYS} days.`);
        return;
      }
      next = { preset: "custom", customStart: start, customEnd: end };
    }
    applyDateConfig(next);
  };

  const selectPreset = (preset: DatePreset) => {
    setSheetError("");
    if (preset === "custom") {
      const range = resolveDateRange(draftConfig, todayDateKey);
      setDraftConfig({
        preset: "custom",
        customStart: draftConfig.customStart ?? range.startDate,
        customEnd: draftConfig.customEnd ?? range.endDate,
      });
      return;
    }
    applyDateConfig({ ...draftConfig, preset });
  };

  useEffect(() => {
    if (!editing || !activeGroupName) return;
    localStorage.setItem(SALES_LAST_GROUP_KEY, activeGroupName);
  }, [editing, activeGroupName]);

  const handleHeaderBack = () => {
    if (editing) {
      cancelEditing();
      return;
    }
    if (selectedDateKey) {
      setSelectedDateKey(null);
      setSaveSuccess("");
      return;
    }
    navigate("/app");
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50">
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleHeaderBack}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>

            <div className="text-[20px] font-semibold">
              {editing ? "Edit Sales" : selectedDateKey ? "Sales Summary" : "Sales History"}
            </div>

            <div className="w-10 h-10" />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 pb-24">
        {!selectedDateKey ? (
          <button
            type="button"
            onClick={openSheet}
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex items-center gap-3 mb-3 text-left"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Calendar size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{presetLabel(dateConfig.preset)}</div>
              <div className="text-xs text-slate-500 truncate">
                {formatRangeLabel(appliedRange.startDate, appliedRange.endDate)}
              </div>
            </div>
            <ChevronDown size={18} className="text-slate-400 shrink-0" />
          </button>
        ) : null}

        {error && !selectedDateKey ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        ) : null}

        {selectedDateKey ? (
          <>
            {detailError ? (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                {detailError}
              </div>
            ) : null}

            {!detailLoading && detailSales ? (
              <div className="mb-3">
                {isSubmitted ? (
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-between gap-2 ${
                      addedByAdmin
                        ? "border-primary/30 bg-primary/5 text-primary"
                        : isFullySettled
                        ? "border-green-200 bg-green-50 text-green-800"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <CheckCircle2
                        size={16}
                        className={
                          addedByAdmin
                            ? "text-primary shrink-0"
                            : isFullySettled
                            ? "text-green-700 shrink-0"
                            : "text-amber-700 shrink-0"
                        }
                      />
                      <span className="truncate">
                        Sales submitted
                        {addedByAdmin ? (
                          <span className="ml-1.5 font-semibold">· Added by CD admin</span>
                        ) : isFullySettled ? (
                          <span className="ml-1.5 font-semibold">· Fully settled</span>
                        ) : (
                          <span className="ml-1.5 font-semibold">
                            · Difference: {formatMoney(remainingAmount)}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        className={`rounded-md border bg-white px-2.5 py-1 text-xs font-semibold ${
                          addedByAdmin
                            ? "border-primary/40 text-primary"
                            : isFullySettled
                            ? "border-green-300 text-green-700"
                            : "border-amber-300 text-amber-700"
                        }`}
                        onClick={() => navigate(`/app/sales/confirm?date=${selectedDateKey}`)}
                      >
                        View
                      </button>
                      {canAmend && !editing ? (
                        <button
                          type="button"
                          className="rounded-md border border-primary/40 bg-white px-2.5 py-1 text-xs font-semibold text-primary"
                          onClick={startEditing}
                        >
                          Edit
                        </button>
                      ) : null}
                    </span>
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600">
                    Sales not submitted
                  </div>
                )}
              </div>
            ) : null}

            {saveSuccess ? (
              <div className="mb-3 rounded-lg border border-green-200 bg-green-50 text-green-700 px-3 py-2 text-sm">
                {saveSuccess}
              </div>
            ) : null}
            {saveError && !showReasonModal ? (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                {saveError}
              </div>
            ) : null}

            {editing ? (
              <div className="pb-16">
                <SalesProductEntryList
                  groupNames={(detailSales?.groups ?? []).map((g) => g.groupName)}
                  groups={detailSales?.groups ?? []}
                  activeGroupName={activeGroupName}
                  onActiveGroupNameChange={setActiveGroupName}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  qtySoldByProductId={qtySoldByProductId}
                  tradeInQtyByProductId={tradeInQtyByProductId}
                  onQtySoldChange={(productId, qty) =>
                    setQtySoldByProductId((prev) => ({ ...prev, [productId]: qty }))
                  }
                  onTradeInQtyChange={(productId, qty) =>
                    setTradeInQtyByProductId((prev) => ({ ...prev, [productId]: qty }))
                  }
                  availableQtyByProductId={availableQtyByProductId}
                  brokenImageProductIds={brokenImageProductIds}
                  onBrokenImage={(productId) =>
                    setBrokenImageProductIds((prev) => ({ ...prev, [productId]: true }))
                  }
                  canEdit
                  loadingGroups={detailLoading}
                />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-slate-700"
                    onClick={cancelEditing}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
                    onClick={() => {
                      if (!hasAmendChanges) {
                        setSaveError("No quantity changes to save.");
                        return;
                      }
                      setSaveError("");
                      setShowReasonModal(true);
                    }}
                    disabled={saving}
                  >
                    Save changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-900">Sales Summary</div>
                  <div className="text-xs text-slate-500">{formatDateWithWeekday(selectedDateKey)}</div>
                </div>
                {detailLoading ? (
                  <div className="text-sm text-slate-500">Loading...</div>
                ) : summaryGroups.length ? (
                  <>
                    {summaryGroups.map((g) => (
                      <div key={g.groupName} className="mb-2">
                        <div className="text-base font-bold text-slate-900 mb-1">{g.groupName}</div>
                        {g.products.map((p) => {
                          const qty = p.qtySold ?? 0;
                          const tradeIn = p.tradeInQty ?? 0;
                          return (
                            <div key={p.productId} className="flex items-center justify-between text-sm py-1 gap-2">
                              <div className="min-w-0 truncate">{p.name}</div>
                              <div className="shrink-0 text-right tabular-nums text-slate-700">
                                {qty > 0 ? <div>{qty}</div> : null}
                                {tradeIn > 0 ? <div className="text-xs text-cyan-700">TI {tradeIn}</div> : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                      <div>
                        <div className="text-xs text-slate-500">Total Quantity Sold</div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums">
                          {totals.totalQty.toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Total Value (RM)</div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums">
                          {formatMoney(totals.totalValue)}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-500 py-2">No products sold for this date.</div>
                )}
              </div>
            )}
          </>
        ) : loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-slate-500">Loading...</div>
        ) : submittedDays.length ? (
          <div className="space-y-2">
            {submittedDays.map((day) => {
              const settled = (day.remainingAmount ?? 0) <= 0.004;
              const dayAddedByAdmin = Boolean(day.addedByAdmin);
              return (
                <button
                  key={day.salesDate}
                  type="button"
                  onClick={() => setSelectedDateKey(day.salesDate)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 flex items-center gap-3 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5 min-w-0">
                      <span className="truncate">{formatDateWithWeekday(day.salesDate)}</span>
                      {day.canAmend ? (
                        <Pencil size={14} className="text-primary shrink-0" aria-label="Editable" />
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      Qty {day.totalQty.toLocaleString()}
                      {day.totalTradeInQty > 0 ? ` · TI ${day.totalTradeInQty}` : ""}
                      {" · "}
                      {formatMoney(day.totalValue)}
                    </div>
                    <div
                      className={`mt-1 text-[11px] font-medium ${
                        dayAddedByAdmin ? "text-primary" : settled ? "text-green-700" : "text-amber-700"
                      }`}
                    >
                      {dayAddedByAdmin
                        ? "Added by CD admin"
                        : settled
                        ? "Fully settled"
                        : `Outstanding ${formatMoney(day.remainingAmount)}`}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400 shrink-0" />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-slate-500">
            No submitted sales for this date range.
          </div>
        )}
      </div>

      {showReasonModal ? (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-end justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-base font-semibold text-slate-900">Reason for amendment</div>
              <div className="text-sm text-slate-600 mt-1">
                This updates stock snapshots and the bank-in difference for this submitted day.
              </div>
            </div>
            <div className="p-4">
              <textarea
                value={amendReason}
                onChange={(e) => setAmendReason(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none"
                placeholder="Why are you changing this sales record?"
              />
              {saveError ? (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                  {saveError}
                </div>
              ) : null}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-slate-700"
                onClick={() => {
                  setShowReasonModal(false);
                  setSaveError("");
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-xl bg-primary text-white py-2.5 text-sm font-semibold disabled:opacity-60"
                onClick={() => void submitAmend()}
                disabled={saving}
              >
                {saving ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {sheetOpen ? (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close date range"
            onClick={() => setSheetOpen(false)}
          />
          <div className="absolute left-0 right-0 bottom-0 mx-auto w-full max-w-md rounded-t-2xl bg-white shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="px-4 pt-3 pb-2 border-b border-gray-100">
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-gray-300" />
              <div className="flex items-center justify-between">
                <div className="text-base font-semibold text-slate-900">Date range</div>
                <button
                  type="button"
                  className="w-8 h-8 rounded-full bg-gray-100 text-slate-600 flex items-center justify-center"
                  onClick={() => setSheetOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="px-4 py-2">
              {PRESET_OPTIONS.map((option) => {
                const selected = draftConfig.preset === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => selectPreset(option.id)}
                    className={`w-full flex items-center justify-between rounded-xl px-3 py-3 mb-1 text-sm ${
                      selected ? "bg-primary/10 text-primary font-semibold" : "text-slate-800"
                    }`}
                  >
                    <span>{option.label}</span>
                    {selected ? <Check size={18} /> : null}
                  </button>
                );
              })}

              {draftConfig.preset === "custom" ? (
                <div className="mt-2 mb-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="min-w-0">
                      <div className="text-xs font-medium text-slate-500 mb-1">Start date</div>
                      <input
                        type="date"
                        max={todayDateKey}
                        value={draftConfig.customStart ?? ""}
                        onChange={(e) =>
                          setDraftConfig((prev) => ({ ...prev, preset: "custom", customStart: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm text-slate-900"
                      />
                    </label>
                    <label className="min-w-0">
                      <div className="text-xs font-medium text-slate-500 mb-1">End date</div>
                      <input
                        type="date"
                        max={todayDateKey}
                        value={draftConfig.customEnd ?? ""}
                        onChange={(e) =>
                          setDraftConfig((prev) => ({ ...prev, preset: "custom", customEnd: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm text-slate-900"
                      />
                    </label>
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">Maximum {HISTORY_MAX_RANGE_DAYS} days.</div>
                </div>
              ) : null}

              {sheetError ? (
                <div className="mb-2 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                  {sheetError}
                </div>
              ) : null}
            </div>

            <div className="px-4 pb-5">
              {draftConfig.preset === "custom" ? (
                <button
                  type="button"
                  onClick={applyDraftConfig}
                  className="w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold"
                >
                  Apply
                </button>
              ) : (
                <div className="h-2" />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
